import { getDownloadURL, ref, uploadBytes } from '@firebase/storage'
import { storage } from 'config/firebase'
import { v4 as uuidv4 } from 'uuid'

/** Upload truck photos for an inventory lead; returns storage paths. */
export async function uploadInventoryLeadTruckPhotos(
  leadId: string,
  truckIndex: number,
  files: File[],
): Promise<string[]> {
  if (!storage) throw new Error('Firebase Storage is not initialized')
  if (!files.length) return []

  const paths: string[] = []
  for (const file of files) {
    const extension = file.name.split('.').pop() || 'jpg'
    const baseName =
      file.name.substring(0, file.name.lastIndexOf('.')) || file.name
    const sanitized = baseName.replace(/[^a-zA-Z0-9._-]/g, '_').replace(/_{2,}/g, '_')
    const fileName = `${Date.now()}_${uuidv4().slice(0, 8)}_${sanitized}.${extension}`
    const storagePath = `inventory_leads/${leadId}/trucks/${truckIndex}/${fileName}`
    const storageRef = ref(storage, storagePath)
    await uploadBytes(storageRef, file)
    paths.push(storagePath)
  }
  return paths
}

/** Resolve download URLs for a list of storage paths (best-effort). */
export async function resolveStoragePathsToUrls(
  paths: string[],
): Promise<Record<string, string>> {
  if (!storage || !paths.length) return {}
  const entries = await Promise.all(
    paths.map(async (path) => {
      try {
        const url = await getDownloadURL(ref(storage, path))
        return [path, url] as const
      } catch {
        return null
      }
    }),
  )
  const out: Record<string, string> = {}
  for (const entry of entries) {
    if (entry) out[entry[0]] = entry[1]
  }
  return out
}

const thumbnailObjectUrlCache = new Map<string, string>()

/**
 * Downscale a remote image for thumbnails so the overview scroll layer does not
 * decode multi-megapixel photos into 104×78 slots. Falls back to the original
 * URL if fetch/canvas is unavailable (e.g. CORS).
 */
export async function downscaleImageUrlForThumbnail(
  url: string,
  maxEdge = 240,
): Promise<string> {
  const cached = thumbnailObjectUrlCache.get(url)
  if (cached) return cached

  try {
    const response = await fetch(url)
    if (!response.ok) return url
    const blob = await response.blob()
    if (!blob.type.startsWith('image/')) return url

    const bitmap = await createImageBitmap(blob)
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height))
    const width = Math.max(1, Math.round(bitmap.width * scale))
    const height = Math.max(1, Math.round(bitmap.height * scale))

    const canvas = document.createElement('canvas')
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return url
    }
    ctx.drawImage(bitmap, 0, 0, width, height)
    bitmap.close()

    const thumbBlob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.72),
    )
    if (!thumbBlob) return url
    const objectUrl = URL.createObjectURL(thumbBlob)
    thumbnailObjectUrlCache.set(url, objectUrl)
    return objectUrl
  } catch {
    return url
  }
}

/** Full download URLs plus matching lightweight thumbnail object URLs. */
export async function resolveStoragePathsToPreviewUrls(
  paths: string[],
): Promise<{ full: Record<string, string>; thumbs: Record<string, string> }> {
  const full = await resolveStoragePathsToUrls(paths)
  const thumbs: Record<string, string> = {}
  await Promise.all(
    Object.entries(full).map(async ([path, url]) => {
      thumbs[path] = await downscaleImageUrlForThumbnail(url)
    }),
  )
  return { full, thumbs }
}
